// Per-player views: what one player is allowed to know. Secrecy is a correctness property,
// so the leak-invariant test checks every view against these rules (spec GP-05, BE-11).
import { OBSERVABLE_GAMES, TILE } from '@wahala/maps';
import { affordancesFor } from './affordances.js';
import { boysConfig, latchedBy, levyOf, payoutOf, purseOf } from './areaboys.js';
import { inArrest, jailed } from './arrest.js';
import { SHAKEN_FACTOR } from './crashes.js';
import { dist2, lineOfSight, TICK_MS } from './geometry.js';
import { hideConfig } from './hide.js';
import { mapOf, STATION_RADIUS } from './match.js';
import { gameOf } from './minigames.js';
import { BOOST_FACTOR, boosted } from './powers.js';
import { ridersOf, SEATS } from './rides.js';
import { abilityOf, copying, disguiseId, marked, markedKeke, mayKnow, shadowed, shielding, specialOf, vitalsOpen, } from './roles.js';
import { rulesFor } from './rules.js';
import { fixesNeeded, lightsOut, liveFixes } from './sabotage.js';
import { nextTimeAt, WEATHER_EFFECT, weatherVision } from './weather.js';
/** The round's numbers for the end screen. Never call it before the round has ended. */
export function roundSummary(s) {
    const summary = {
        durationMs: s.tick * TICK_MS,
        players: s.order.map((id) => {
            const p = s.players[id];
            const row = {
                id,
                reports: p.stats.reports,
                rightVotes: p.stats.correctVotes,
            };
            if (p.role === 'impostor')
                row.knocks = p.stats.kills;
            else
                row.stops = { done: p.stats.tasksDone, total: (s.tasks[id] ?? []).length };
            if (s.rides) {
                row.trips = s.rides.trips[id] ?? 0;
                row.fares = s.rides.earned[id] ?? 0;
            }
            if (s.areaBoys)
                Object.assign(row, payoutOf(s, id));
            return row;
        }),
    };
    // the mode's own numbers (a race: places, times, laps, pit stops)
    rulesFor(s).extendSummary?.(s, summary);
    return summary;
}
/** Vision radius in world units for this viewer. */
export function visionRadius(s, viewer) {
    const tiles = viewer.role === 'impostor' ? s.config.impostorVision : s.config.crewVision;
    // the mode may set the factor outright (Powers' wiper clears NEPA darkness around you); otherwise
    // NEPA leaves drivers a headlamp beam
    const factor = rulesFor(s).visionFactor(s, viewer);
    // the weather scales everyone alike; NEPA and the wiper multiply on top
    const sky = weatherVision(s);
    if (factor !== null)
        return Math.round(tiles * TILE * factor * sky);
    return Math.round(tiles * TILE * (lightsOut(s, viewer) ? s.config.lightsOutFactor : 1) * sky);
}
/** Can a living viewer see a world position right now? (radius, then walls; `r` in place of their vision) */
export function canSee(s, viewer, at, r = visionRadius(s, viewer)) {
    if (dist2(viewer.pos, at) > r * r)
        return false;
    return lineOfSight(mapOf(s), viewer.pos, at);
}
/** Ids of players whose position this viewer may receive. Asymmetric by design. */
export function visibleTo(s, viewerId) {
    const viewer = Object.hasOwn(s.players, viewerId) ? s.players[viewerId] : undefined;
    const out = new Set();
    if (!viewer)
        return out;
    const all = rulesFor(s).seesAll?.(s) ?? false;
    for (const id of s.order) {
        const p = s.players[id];
        if (id === viewerId || p.departed)
            continue;
        if (!viewer.alive || s.phase === 'ENDED' || all) {
            out.add(id); // ghosts and the end screen see everyone (and in a race, the minimap does)
            continue;
        }
        if (!p.alive || p.vent !== null)
            continue; // ghosts and vented players are invisible to the living
        if (s.phase !== 'PLAYING')
            continue; // meetings show rows, not positions
        if (rulesFor(s).hiddenFrom(s, viewer, p))
            continue; // a shadow, out of a driver's sight
        if (canSee(s, viewer, p.pos))
            out.add(id);
    }
    return out;
}
function roleKnown(s, viewer, other) {
    if (s.phase === 'ENDED')
        return true;
    if (viewer.role === 'impostor' && other.role === 'impostor')
        return true;
    if (rulesFor(s).roleOpen(s, other))
        return true;
    if (!viewer.alive && s.config.ghostsSeeRoles)
        return true;
    if (other.ejected && s.config.confirmEjections)
        return true;
    return false;
}
function openSession(x) {
    if (!x)
        return null;
    const out = { taskId: x.taskId, startedAt: x.startedAt };
    if (x.wires)
        out.wires = [...x.wires];
    if (x.code !== undefined)
        out.code = x.code;
    if (x.fuel)
        out.fuel = { ...x.fuel };
    if (x.pumps !== undefined)
        out.pumps = x.pumps;
    if (x.plus)
        out.plus = x.plus;
    if (x.waitMs !== undefined)
        out.waitMs = x.waitMs;
    if (x.odd !== undefined)
        out.odd = x.odd;
    if (x.spot)
        out.spot = { ...x.spot };
    if (x.notes)
        out.notes = [...x.notes];
    if (x.dirt)
        out.dirt = [...x.dirt];
    return out;
}
/** The player is at their stop playing a game others can watch (any side: see OtherPlayerView). */
export function scanning(s, p) {
    const open = s.sessions[p.id];
    if (!open || !p.alive || p.vent !== null || s.phase !== 'PLAYING')
        return false;
    const task = s.tasks[p.id]?.find((t) => t.id === open.taskId);
    if (!task || task.done || !OBSERVABLE_GAMES.includes(gameOf(task)))
        return false;
    const at = mapOf(s).stations.find((x) => x.id === task.stationId);
    return !!at && dist2(p.pos, at.pos) <= STATION_RADIUS * STATION_RADIUS;
}
/** The id this viewer knows a keke by: a copy-copy in disguise by its disguise id, never its seat. */
function shownAs(s, v, id) {
    const p = s.players[id];
    if (!p || p.id === v.id || !s.roles)
        return id;
    return copying(s, p) && !mayKnow(s, v, p) ? disguiseId(s, id) : id;
}
export function buildView(s, viewerId) {
    const v = Object.hasOwn(s.players, viewerId) ? s.players[viewerId] : undefined;
    if (!v)
        throw new Error(`unknown viewer ${viewerId}`);
    const visible = visibleTo(s, viewerId);
    const truth = !v.alive || s.phase === 'ENDED';
    const players = [];
    const disguises = [];
    for (const id of s.order) {
        if (id === viewerId)
            continue;
        const p = s.players[id];
        const o = {
            id: p.id,
            seat: p.seat,
            colour: p.colour,
            name: p.name,
            alive: truth ? p.alive : p.alive || !p.deathRevealed,
            connected: p.connected,
        };
        if (visible.has(id)) {
            o.pos = { ...p.pos };
            if (boosted(s, id))
                o.boost = true;
            if (scanning(s, p))
                o.scanning = true;
            // in an arrest, both kekes stand locked together: anyone in sight sees it
            if (inArrest(s, id))
                o.arrested = true;
            if (s.arrest && Object.hasOwn(s.arrest.cuts, id))
                o.cutting = true;
        }
        // in the yard: seen there, or shown to the park by a meeting (a spirit and the end see it all)
        if (jailed(s, id) && (visible.has(id) || truth || s.arrest?.revealed[id]))
            o.jailed = true;
        if (roleKnown(s, v, p))
            o.role = p.role;
        if (s.phase === 'VOTING' && p.alive)
            o.voted = p.vote !== null;
        if (p.ejected)
            o.ejected = true;
        if (p.departed)
            o.departed = true;
        if (s.roles) {
            const known = mayKnow(s, v, p);
            const role = specialOf(s, p.id);
            if (role && known)
                o.special = role;
            const copy = copying(s, p);
            if (copy && known)
                o.copy = copy.id;
            else if (copy) {
                // dressed as someone else: the real row stays where it always is, as a keke out of sight,
                // and the keke on the road is a separate row under an id nobody can trace back (below)
                if (o.pos)
                    disguises.push({ p, copy, pos: o.pos, boost: o.boost === true });
                delete o.pos;
                delete o.boost;
                delete o.scanning;
                delete o.arrested;
                delete o.cutting;
            }
            if (shadowed(s, p) && known)
                o.shadow = true;
            // an area boy's shield shows on the keke as they see it (a disguise's own row)
            if (shielding(s, v) === p.id && !(copy && !known))
                o.shield = true;
        }
        players.push(o);
    }
    // a copy-copy in sight: the copy's colour (and so plate), name and seat, the copy's connection,
    // a living keke, and an opaque id; after the roster rows, in a fixed order that says nothing
    disguises.sort((a, b) => (disguiseId(s, a.p.id) < disguiseId(s, b.p.id) ? -1 : 1));
    for (const d of disguises) {
        const o = {
            id: disguiseId(s, d.p.id),
            seat: d.copy.seat,
            colour: d.copy.colour,
            name: d.copy.name,
            alive: true,
            connected: d.copy.connected,
            pos: d.pos,
        };
        if (d.boost)
            o.boost = true;
        if (scanning(s, d.p))
            o.scanning = true;
        if (inArrest(s, d.p.id))
            o.arrested = true;
        if (shielding(s, v) === d.p.id)
            o.shield = true;
        players.push(o);
    }
    const bodies = s.phase === 'PLAYING'
        ? s.bodies
            .filter((b) => !v.alive || canSee(s, v, b.pos))
            .map((b) => ({ id: b.id, pos: { ...b.pos }, ...(b.clamp ? { clamp: true } : {}) }))
        : [];
    let meeting = null;
    if (s.meeting) {
        const m = s.meeting;
        meeting = {
            calledBy: m.calledBy,
            reason: m.reason,
            discussionEndsAt: m.discussionEndsAt,
            votingEndsAt: m.votingEndsAt,
            revealEndsAt: m.revealEndsAt,
            result: null,
        };
        if (m.result) {
            const r = {
                ejected: m.result.ejected,
                tally: m.result.tally.map((x) => ({ ...x })),
            };
            if (!s.config.anonymousVotes) {
                r.votes = s.order
                    .map((id) => s.players[id])
                    .filter((p) => p.vote !== null)
                    .map((p) => ({ voter: p.id, choice: p.vote }));
            }
            if (m.result.ejected && s.config.confirmEjections) {
                r.ejectedRole = s.players[m.result.ejected].role;
            }
            meeting.result = r;
        }
    }
    const you = {
        id: v.id,
        seat: v.seat,
        colour: v.colour,
        name: v.name,
        role: v.role,
        alive: v.alive,
        ejected: v.ejected,
        pos: { ...v.pos },
        vision: visionRadius(s, v),
        vel: { ...v.vel },
        lastInputSeq: v.lastInputSeq,
        meetingsLeft: v.meetingsLeft,
        // an agbero's task list looks exactly like a driver's: no fake flag, same shape
        tasks: (s.tasks[v.id] ?? []).map((t) => ({
            id: t.id,
            stationId: t.stationId,
            label: t.label,
            holdMs: t.holdMs,
            done: t.done,
            game: gameOf(t),
            // every stop carries its stage, so a list's shape never depends on which stops were dealt
            stage: t.stage ?? 0,
            stages: t.stages ?? 1,
            readyAt: t.readyAt ?? 0,
        })),
        // your open stop, with what its game needs (dealt by the server, never anyone else's)
        session: openSession(s.sessions[v.id] ?? null),
        vote: v.vote,
        vent: v.vent,
    };
    if (v.role === 'impostor') {
        you.killReadyAt = v.killReadyAt;
        you.sabotageReadyAt = s.sabotageReadyAt ?? 0;
        you.ventReadyAt = v.ventCooldownUntil;
    }
    const pw = s.powers;
    if (pw) {
        you.power = {
            held: pw.held[v.id] ?? null,
            boostUntil: pw.boostUntil[v.id] ?? 0,
            wiperUntil: pw.wiperUntil[v.id] ?? 0,
        };
    }
    let impostorCount = 0;
    let taskTotal = 0;
    for (const id of s.order) {
        const p = s.players[id];
        if (p.role === 'impostor')
            impostorCount++;
        else if (!p.departed)
            for (const t of s.tasks[id] ?? [])
                if (!t.fake)
                    taskTotal++;
    }
    const view = {
        tick: s.tick,
        now: s.now,
        phase: s.phase,
        you,
        players,
        bodies,
        // what the host's taskBar setting lets players see; the end screen tells the truth
        taskProgress: s.phase === 'ENDED' ? s.taskProgress : s.shownProgress,
        taskBar: s.config.taskBar,
        vioCount: impostorCount,
        taskTotal,
        meeting,
        winner: s.phase === 'ENDED' ? s.winner : null,
        hornReadyAt: s.meetingCooldownUntil,
        sabotage: s.sabotage && s.phase !== 'ENDED'
            ? {
                kind: s.sabotage.kind,
                endsAt: s.sabotage.endsAt,
                fixed: liveFixes(s),
                need: fixesNeeded(s.sabotage.kind),
            }
            : null,
        // only what this view already shows can be a target
        affordances: affordancesFor(s, v, players, bodies),
    };
    if (s.roles) {
        const role = specialOf(s, v.id);
        const a = abilityOf(s, v.id);
        if (role && a) {
            you.special = role;
            you.ability = {
                cooldownUntil: a.cooldownUntil,
                activeUntil: a.activeUntil,
                charges: a.charges,
                target: a.target,
            };
            // the conductor's own shortcut cooldown, as an agbero's
            if (role === 'engineer')
                you.ventReadyAt = v.ventCooldownUntil;
        }
        view.alarms =
            s.phase === 'PLAYING'
                ? s.roles.alarms.filter((x) => x.until > s.now).map((x) => ({ ...x, pos: { ...x.pos } }))
                : [];
        if (vitalsOpen(s, v))
            view.vitals = s.order.map((id) => ({ id, alive: s.players[id].alive }));
        const mark = marked(s, v);
        const t = markedKeke(s, v);
        if (mark && t) {
            // the id it was marked by: a disguise stays a disguise on the spotter's map
            view.tracked = { id: mark };
            if (t.alive && !t.departed && t.vent === null && !rulesFor(s).hiddenFrom(s, v, t))
                view.tracked.pos = { ...t.pos };
        }
    }
    if (s.phase === 'ENDED')
        view.summary = roundSummary(s);
    // the mode's own part of the view (a race: your lap, place, fuel, keke and the road you can see)
    rulesFor(s).extendView?.(s, v, view, (pos, r) => !v.alive || canSee(s, v, pos, r));
    const h = s.hide;
    if (h) {
        const c = hideConfig(s);
        view.hide = {
            endsAt: h.endsAt,
            finalAt: h.endsAt - c.finalMs,
            seekingAt: h.startedAt + c.headStartMs,
            finalSpeed: c.finalSpeed,
            escapes: h.escapes[v.id] ?? 0,
            escapeEndsAt: h.escapeEndsAt[v.id] ?? 0,
            ventReadyAt: v.role === 'crew' ? v.ventCooldownUntil : 0,
        };
    }
    const r = s.rides;
    if (r) {
        you.ride = {
            aboard: ridersOf(s, v.id).map((x) => ({ to: x.to, fare: x.fare })),
            seats: SEATS,
            earned: r.earned[v.id] ?? 0,
            trips: r.trips[v.id] ?? 0,
        };
        const map = mapOf(s);
        view.waiting =
            s.phase === 'PLAYING'
                ? map.stations
                    .filter((st) => (r.waiting[st.id]?.length ?? 0) > 0 && (!v.alive || canSee(s, v, st.pos)))
                    .map((st) => ({ stationId: st.id, riders: r.waiting[st.id]?.length ?? 0 }))
                : [];
    }
    if (s.weather) {
        view.weather = { kind: s.weather.kind };
        if (s.weather.turn)
            view.weather.turn = { ...s.weather.turn };
    }
    if (s.daylight) {
        view.daylight = { time: s.daylight.time, since: s.daylight.changedAt };
        const next = nextTimeAt(s);
        if (next !== null)
            view.daylight.next = next;
    }
    const cr = s.crashes;
    if (cr) {
        const until = cr.shakenUntil[v.id] ?? 0;
        if (until > s.now)
            you.shakenUntil = until;
        const seen = s.phase === 'PLAYING' ? cr.recent.filter((x) => !v.alive || canSee(s, v, x.pos)) : [];
        if (seen.length)
            view.crashes = seen.map((x) => ({ id: x.id, pos: { ...x.pos }, at: x.at }));
    }
    const ab = s.areaBoys;
    if (ab) {
        const prompt = ab.prompt[v.id] ?? null;
        const coins = purseOf(s, v.id);
        const on = latchedBy(s, v.id);
        you.purse = {
            coins,
            banked: ab.banked[v.id] ?? 0,
            ticket: ab.ticket[v.id] ?? false,
            latched: on
                ? { boy: on.id, until: on.onUntil, pinned: on.pinned, share: boysConfig(s).latchSpeed, coins: levyOf(s, v.id) }
                : null,
            prompt: prompt ? { boy: prompt.boy, line: on?.pinned ? 'here' : 'settle', coins: levyOf(s, v.id) } : null,
        };
        if (v.role === 'impostor')
            you.purse.sends = ab.sends[v.id] ?? 0;
        const playing = s.phase === 'PLAYING';
        const ear = boysConfig(s).bangRadius * TILE;
        view.areaBoys = {
            inSight: playing
                ? ab.boys
                    .filter((b) => !v.alive || canSee(s, v, b.pos))
                    .map((b) => ({
                    id: b.id,
                    pos: { ...b.pos },
                    // the keke he hangs off, when that keke is in sight too
                    ...(b.on !== null && (b.on === v.id || visible.has(b.on)) ? { on: shownAs(s, v, b.on) } : {}),
                    ...(b.razaq ? { razaq: true } : {}),
                }))
                : [],
            posts: ab.boys.map((b) => b.post).sort((a, b) => a - b),
            bangs: playing
                ? ab.bangs
                    .filter((x) => x.until > s.now && (!v.alive || dist2(v.pos, x.pos) <= ear * ear))
                    .map((x) => ({ pos: { ...x.pos }, until: x.until }))
                : [],
        };
    }
    const ar = s.arrest;
    if (ar) {
        const c = s.config.arrest;
        const open = inArrest(s, v.id);
        if (open && c) {
            const driver = Object.hasOwn(ar.open, v.id);
            you.arrest = driver
                ? {
                    side: 'driver',
                    with: shownAs(s, v, open.by),
                    startedAt: open.startedAt,
                    endsAt: open.endsAt,
                    needle: open.needle,
                    lawyer: c.lawyer,
                    sweet: c.sweet,
                }
                : {
                    side: 'officer',
                    with: shownAs(s, v, Object.keys(ar.open).find((k) => ar.open[k]?.by === v.id)),
                    startedAt: open.startedAt,
                    endsAt: open.endsAt,
                };
        }
        if (jailed(s, v.id))
            you.jailed = true;
        const cut = Object.hasOwn(ar.cuts, v.id) ? ar.cuts[v.id] : undefined;
        if (cut && c)
            you.cut = { stage: cut.stage, of: c.cuts, at: cut.at, ms: c.cutMs };
        // arrests under way in sight: a spot for the siren, never who
        view.arrests =
            s.phase === 'PLAYING'
                ? Object.keys(ar.open)
                    .sort()
                    .map((k) => ar.open[k]?.pos)
                    .filter((pos) => !v.alive || canSee(s, v, pos))
                    .map((pos) => ({ pos: { ...pos } }))
                : [];
    }
    if (pw) {
        view.pickups =
            s.phase === 'PLAYING'
                ? pw.pickups.filter((x) => !v.alive || canSee(s, v, x.pos)).map((x) => ({ id: x.id, pos: { ...x.pos } }))
                : [];
    }
    return view;
}
/**
 * Your own keke's speed per tick from what your view says, exactly as the server's rule set sets
 * it (the phone predicts with this): a power-up boost, or in hide and seek the agbero standing still
 * for the head start and driving faster in the final stretch.
 */
/** Your own keke's braking from what your view says (the weather), exactly as the server steps it. */
export const viewBrake = (view) => WEATHER_EFFECT[view.weather?.kind ?? 'day'].brake;
export function viewSpeed(view, base) {
    if (!view.you.alive)
        return base;
    // City Hustle: the server's own speed for the keke (fuel, dents, a puncture, an agbero, the expressway)
    if (view.hustle)
        return view.hustle.held ? 0 : view.hustle.speed;
    // the rule set's speed first, then shaken after a crash on top, exactly as the server steps it
    const shaken = (v) => ((view.you.shakenUntil ?? 0) > view.now ? Math.round(v * SHAKEN_FACTOR) : v);
    // in an arrest or in the yard: standing still
    if (view.you.arrest || view.you.jailed)
        return 0;
    // an agbero hanging off the keke: the mode's speed at his share, then shaken on top, as the server rounds it
    const latch = view.you.purse?.latched;
    if (latch) {
        const mode = view.you.power && view.you.power.boostUntil > view.now ? Math.round(base * BOOST_FACTOR) : base;
        return shaken(Math.round(mode * latch.share));
    }
    const h = view.hide;
    if (h && view.you.role === 'impostor') {
        if (view.now < h.seekingAt)
            return 0;
        if (view.now >= h.finalAt)
            return shaken(Math.round(base * h.finalSpeed));
        return shaken(base);
    }
    return shaken(view.you.power && view.you.power.boostUntil > view.now ? Math.round(base * BOOST_FACTOR) : base);
}
//# sourceMappingURL=views.js.map